import { useCallback, useEffect, useMemo, useState } from "react";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { polygon } from "viem/chains";
import { Address, encodeFunctionData, isAddressEqual } from "viem";
import { Boxes, ChevronRight, Loader2, Network, RefreshCw, Undo2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import {
  INCEPTION_ERC721_WITHDRAW_ABI,
  INCEPTION_FACTORY_ABI,
  INCEPTION_FACTORY_ADDRESS,
  INCEPTION_VAULT_ABI,
} from "@/features/inception/config";

const MAX_DEPTH = 8;
const MAX_ASSETS_PER_VAULT = 100;

type VaultNode = {
  vault: Address;
  tokenId: bigint;
  path: Address[];
  children: VaultNode[];
  cycle?: boolean;
  truncated?: boolean;
  unreadable?: boolean;
};

const compact = (address: Address) => `${address.slice(0, 6)}…${address.slice(-4)}`;

const errorMessage = (error: unknown) => {
  if (!error || typeof error !== "object") return "The transaction was not completed.";
  const candidate = error as { shortMessage?: string; message?: string };
  return candidate.shortMessage || candidate.message || "The transaction was not completed.";
};

const flatten = (nodes: VaultNode[]): VaultNode[] => nodes.flatMap((node) => [node, ...flatten(node.children)]);

function TreeNode({ node, busy, onDetach }: { node: VaultNode; busy: boolean; onDetach: (node: VaultNode) => void }) {
  const nested = node.path.length > 1;
  return (
    <li className={nested ? "ml-5 border-l pl-4" : ""}>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card p-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Boxes className="h-4 w-4 text-primary" />
            <span>{nested ? "Nested vault" : "Top-level vault"}</span>
            <span className="text-xs font-normal text-muted-foreground">depth {node.path.length - 1}</span>
          </div>
          <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{node.vault}</p>
          <p className="mt-1 text-xs text-muted-foreground">Token {node.tokenId.toString()} · {node.children.length} child vault{node.children.length === 1 ? "" : "s"}</p>
          {nested && <p className="mt-1 text-xs text-muted-foreground">Path: {node.path.map(compact).join(" → ")}</p>}
        </div>
        {nested && !node.cycle && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onDetach(node)}>
            <Undo2 className="mr-2 h-4 w-4" />Move to top level
          </Button>
        )}
      </div>
      {node.cycle && <p className="mt-2 text-xs text-destructive">Cycle detected. Do not submit further nesting transactions for this branch.</p>}
      {node.unreadable && <p className="mt-2 text-xs text-destructive">This vault's recorded assets could not be read.</p>}
      {node.truncated && <p className="mt-2 text-xs text-muted-foreground">Only the first {MAX_ASSETS_PER_VAULT} recorded assets were inspected.</p>}
      {node.children.length > 0 && <ul className="mt-3 space-y-3">{node.children.map((child) => <TreeNode key={child.vault} node={child} busy={busy} onDetach={onDetach} />)}</ul>}
    </li>
  );
}

export function NestedOwnershipPanel({ roots, onChanged }: { roots: Address[]; onChanged: () => Promise<void> | void }) {
  const { address, chainId } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const { toast } = useToast();
  const [tree, setTree] = useState<VaultNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [childVault, setChildVault] = useState<Address>();
  const [parentVault, setParentVault] = useState<Address>();
  const [acknowledged, setAcknowledged] = useState(false);

  const loadTree = useCallback(async () => {
    if (!publicClient || !INCEPTION_FACTORY_ADDRESS || roots.length === 0) {
      setTree([]);
      return;
    }
    setLoading(true);
    const readNode = async (vault: Address, tokenId: bigint, path: Address[], depth: number): Promise<VaultNode> => {
      if (path.slice(0, -1).some((ancestor) => isAddressEqual(ancestor, vault))) {
        return { vault, tokenId, path, children: [], cycle: true };
      }
      if (depth >= MAX_DEPTH) return { vault, tokenId, path, children: [], truncated: true };
      try {
        const count = await publicClient.readContract({ address: vault, abi: INCEPTION_VAULT_ABI, functionName: "ownedAssetsCount" } as never) as bigint;
        const inspected = Number(count > BigInt(MAX_ASSETS_PER_VAULT) ? BigInt(MAX_ASSETS_PER_VAULT) : count);
        const assets = await Promise.all(Array.from({ length: inspected }, (_, index) => publicClient.readContract({
          address: vault,
          abi: INCEPTION_VAULT_ABI,
          functionName: "ownedAssetAt",
          args: [BigInt(index)],
        } as never) as Promise<readonly [Address, bigint]>));
        const childIds = assets
          .filter(([token]) => isAddressEqual(token, INCEPTION_FACTORY_ADDRESS))
          .map(([, childId]) => childId);
        const children = (await Promise.all(childIds.map(async (childId) => {
          try {
            const child = await publicClient.readContract({
              address: INCEPTION_FACTORY_ADDRESS,
              abi: INCEPTION_FACTORY_ABI,
              functionName: "collectionAddress",
              args: [childId],
            } as never) as Address;
            return readNode(child, childId, [...path, child], depth + 1);
          } catch {
            return undefined;
          }
        }))).filter((child): child is VaultNode => child !== undefined);
        return { vault, tokenId, path, children, truncated: count > BigInt(MAX_ASSETS_PER_VAULT) };
      } catch {
        return { vault, tokenId, path, children: [], unreadable: true };
      }
    };

    try {
      const rootsWithIds = await Promise.all(roots.map(async (vault) => ({
        vault,
        tokenId: await publicClient.readContract({
          address: INCEPTION_FACTORY_ADDRESS,
          abi: INCEPTION_FACTORY_ABI,
          functionName: "tokenIdForCollection",
          args: [vault],
        } as never) as bigint,
      })));
      const nextTree = await Promise.all(rootsWithIds.map(({ vault, tokenId }) => readNode(vault, tokenId, [vault], 0)));
      setTree(nextTree);
      setChildVault((current) => current && roots.some((root) => isAddressEqual(root, current)) ? current : roots[0]);
    } catch (error) {
      toast({ title: "Unable to load nested ownership", description: errorMessage(error), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [publicClient, roots, toast]);

  useEffect(() => { void loadTree(); }, [loadTree]);

  const allNodes = useMemo(() => flatten(tree), [tree]);
  const eligibleParents = useMemo(() => {
    if (!childVault) return allNodes;
    return allNodes.filter((node) => !isAddressEqual(node.vault, childVault) && !isAddressEqual(node.path[0], childVault));
  }, [allNodes, childVault]);

  useEffect(() => {
    setParentVault((current) => current && eligibleParents.some((node) => isAddressEqual(node.vault, current)) ? current : eligibleParents[0]?.vault);
  }, [eligibleParents]);

  const requireSigner = () => {
    if (!address || !walletClient || !publicClient) throw new Error("Connect and unlock the owner wallet.");
    if (chainId !== polygon.id) throw new Error("Switch the connected wallet to Polygon (chain 137).");
    if (!INCEPTION_FACTORY_ADDRESS) throw new Error("The Inception factory is not configured.");
    return { account: address, client: walletClient, reader: publicClient, factory: INCEPTION_FACTORY_ADDRESS };
  };

  const nest = async () => {
    if (!childVault || !parentVault || !acknowledged) return;
    setBusy(true);
    try {
      const { account, client, reader, factory } = requireSigner();
      const child = tree.find((node) => isAddressEqual(node.vault, childVault));
      if (!child) throw new Error("Only a top-level token owned by the connected wallet can be nested.");
      const target = allNodes.find((node) => isAddressEqual(node.vault, parentVault));
      if (!target || isAddressEqual(target.path[0], childVault)) throw new Error("That parent would create an ownership cycle.");
      const request = await reader.simulateContract({
        address: factory,
        abi: INCEPTION_FACTORY_ABI,
        functionName: "safeTransferFrom",
        args: [account, parentVault, child.tokenId],
        account,
      });
      const hash = await client.writeContract(request.request);
      const receipt = await reader.waitForTransactionReceipt({ hash });
      if (receipt.status !== "success") throw new Error("The nesting transaction reverted on Polygon.");
      setAcknowledged(false);
      await onChanged();
      await loadTree();
      toast({ title: "Vault nested", description: `${compact(childVault)} is now controlled through ${compact(parentVault)}.` });
    } catch (error) {
      toast({ title: "Nesting failed", description: errorMessage(error), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const detach = async (node: VaultNode) => {
    setBusy(true);
    try {
      const { account, client, reader, factory } = requireSigner();
      if (node.path.length < 2) throw new Error("This vault is already top level.");
      const parentPath = node.path.slice(0, -1);
      const immediateParent = parentPath[parentPath.length - 1];
      const withdrawData = encodeFunctionData({
        abi: INCEPTION_ERC721_WITHDRAW_ABI,
        functionName: "safeTransferTo",
        args: [factory, node.tokenId, account],
      });

      let request;
      if (parentPath.length === 1) {
        request = await reader.simulateContract({
          address: immediateParent,
          abi: INCEPTION_ERC721_WITHDRAW_ABI,
          functionName: "safeTransferTo",
          args: [factory, node.tokenId, account],
          account,
        });
      } else {
        let target = immediateParent;
        let payload = withdrawData;
        for (let index = parentPath.length - 2; index >= 1; index -= 1) {
          payload = encodeFunctionData({ abi: INCEPTION_VAULT_ABI, functionName: "externalCall", args: [target, payload] });
          target = parentPath[index];
        }
        request = await reader.simulateContract({
          address: parentPath[0],
          abi: INCEPTION_VAULT_ABI,
          functionName: "externalCall",
          args: [target, payload],
          account,
        });
      }
      const hash = await client.writeContract(request.request);
      const receipt = await reader.waitForTransactionReceipt({ hash });
      if (receipt.status !== "success") throw new Error("The unnesting transaction reverted on Polygon.");
      await onChanged();
      await loadTree();
      toast({ title: "Vault moved to top level", description: `${compact(node.vault)} is owned directly by your wallet again.` });
    } catch (error) {
      toast({ title: "Unable to move vault", description: errorMessage(error), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  if (!INCEPTION_FACTORY_ADDRESS || roots.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2"><Network className="h-5 w-5" />Nested ownership</CardTitle>
            <CardDescription>See which vault controls each child and safely create or unwind ownership branches.</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={loadTree} disabled={loading || busy}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh tree
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <AlertTitle>Control follows the branch</AlertTitle>
          <AlertDescription>
            <span className="block">Root owner: <span className="break-all font-mono">{address}</span></span>
            <span className="mt-1 block">A nested token is no longer owned directly by your wallet. Actions must pass through every ancestor vault; moving it back to top level reverses that relationship.</span>
          </AlertDescription>
        </Alert>

        {loading ? <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Reading ownership branches from Polygon…</p> : (
          <ul className="space-y-3">{tree.map((node) => <TreeNode key={node.vault} node={node} busy={busy} onDetach={detach} />)}</ul>
        )}

        {tree.length > 1 && (
          <div className="space-y-4 rounded-md border p-4">
            <div><h3 className="font-medium">Nest a top-level vault</h3><p className="text-xs text-muted-foreground">Choose the ownership token to move, then the vault that should become its immediate parent.</p></div>
            <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-end">
              <div className="space-y-2">
                <Label htmlFor="nested-child">Child token</Label>
                <select id="nested-child" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={childVault} onChange={(event) => { setChildVault(event.target.value as Address); setAcknowledged(false); }}>
                  {tree.map((node) => <option key={node.vault} value={node.vault}>{compact(node.vault)} · token {node.tokenId.toString()}</option>)}
                </select>
              </div>
              <ChevronRight className="hidden h-5 w-5 text-muted-foreground md:block" />
              <div className="space-y-2">
                <Label htmlFor="nested-parent">Parent vault</Label>
                <select id="nested-parent" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={parentVault} onChange={(event) => { setParentVault(event.target.value as Address); setAcknowledged(false); }}>
                  {eligibleParents.map((node) => <option key={node.vault} value={node.vault}>{compact(node.vault)} · depth {node.path.length - 1}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input className="mt-1" type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
              <span>I understand the child token will leave my wallet and its vault will be controlled through the selected parent branch.</span>
            </label>
            <Button onClick={nest} disabled={busy || loading || !childVault || !parentVault || !acknowledged}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Nest ownership token
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
